import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Event as EventIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import eventService from '../../services/eventService';

const StatCard = ({ title, value, icon, color, trend, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flexGrow={1}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <LinearProgress sx={{ my: 2, maxWidth: 100 }} />
            ) : (
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {value}
              </Typography>
            )}
            {trend && (
              <Chip
                label={trend}
                size="small"
                color={trend.includes('+') ? 'success' : 'default'}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}20`,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalRSVPs: 0,
    totalCheckIns: 0,
    activeEvents: 0,
  });

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const response = await eventService.getDashboardStats();

        if (response.success) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        // Keep zeros on error
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  const statsCards = [
    {
      title: 'Total Events',
      value: stats.totalEvents,
      icon: <EventIcon sx={{ fontSize: 32 }} />,
      color: '#667eea',
    },
    {
      title: 'Total RSVPs',
      value: stats.totalRSVPs,
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: '#f093fb',
    },
    {
      title: 'Check-ins',
      value: stats.totalCheckIns,
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
      color: '#4facfe',
    },
    {
      title: 'Active Events',
      value: stats.activeEvents,
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      color: '#43e97b',
    },
  ];

  return (
    <Box>
      {/* Welcome Section */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          background: (theme) => theme.custom.gradients.primary,
          color: 'white',
        }}
      >
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Welcome back, {user?.firstName}!
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Here's what's happening with your events today
        </Typography>
      </Paper>

      {/* Stats Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {statsCards.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </Box>

      {/* Quick Actions / Recent Activity */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Recent Activity
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={6}
            color="text.secondary"
          >
            <Typography variant="body2">
              Activity feed coming soon...
            </Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Upcoming Events
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={6}
            color="text.secondary"
          >
            <Typography variant="body2">
              No upcoming events
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
